import React, { useEffect, useState } from 'react';
import { View, RefreshControl, Text } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { LineChart } from 'react-native-chart-kit';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchDashboard } from '../redux/slices/dashboardSlice';
import StatCard from '../components/StatCard';
import { Card, PageHeader, Screen, SectionHeader } from '../components/ui';
import { RADIUS } from '../assets/theme';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import { useLayout } from '../theme/useLayout';
import type { RootStackParamList } from '../navigation/AppNavigator';

function fmt(n?: number) {
  return (n ?? 0).toLocaleString('fr-FR') + ' DH';
}

function greeting() {
  const hour = new Date().getHours();
  return hour < 18 ? 'Bonjour' : 'Bonsoir';
}

type Shortcut = {
  label: string;
  hint: string;
  icon: React.ComponentProps<typeof MaterialCommunityIcons>['name'];
  onPress: () => void;
};

export default function EcranAccueil() {
  const dispatch = useAppDispatch();
  const styles = useStyles();
  const { colors } = useAppTheme();
  const { isDesktop } = useLayout();
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const { data, loading } = useAppSelector((s) => s.dashboard);
  const username = useAppSelector((s) => s.auth.username);

  const load = () => { dispatch(fetchDashboard()); };

  useEffect(() => { load(); }, []);

  const chartLabels = data?.evolutionMensuelle?.map((m) => m.periode.slice(-2)) ?? [];
  const chartData = data?.evolutionMensuelle?.map((m) => Number(m.montant)) ?? [];
  const [chartWidth, setChartWidth] = useState(0);

  const shortcuts: Shortcut[] = [
    {
      label: 'Nouvelle commande',
      hint: 'Devis, bon de commande ou facture',
      icon: 'file-document-edit-outline',
      onPress: () => navigation.navigate('NouvelleCommande', {}),
    },
    {
      label: 'Clients',
      hint: 'Fiches, véhicules et soldes',
      icon: 'account-group-outline',
      onPress: () => navigation.navigate('Main', { screen: 'ListeClients' }),
    },
    {
      label: 'Parc véhicules',
      hint: 'Boîtiers, abonnements, renouvellements',
      icon: 'car-outline',
      onPress: () => navigation.navigate('Main', { screen: 'ParkVehicules' }),
    },
  ];

  const kpis = [
    { label: "Chiffre d'affaires", value: fmt(data?.chiffreAffairesTotal), icon: 'cash-multiple' as const, tone: 'primary' as const },
    { label: 'Avances reçues', value: fmt(data?.totalAvances), icon: 'hand-coin-outline' as const, tone: 'brand' as const },
    { label: 'Reste à recouvrer', value: fmt(data?.totalResteARecouvrer), icon: 'clock-alert-outline' as const, tone: 'danger' as const },
    { label: 'Dépenses', value: fmt(data?.totalDepenses), icon: 'credit-card-outline' as const, tone: 'warning' as const },
  ];

  return (
    <Screen
      refreshControl={
        <RefreshControl refreshing={loading} onRefresh={load} colors={[colors.primary]} tintColor={colors.primary} />
      }
    >
      <PageHeader
        eyebrow={`${greeting()}${username ? `, ${username}` : ''}`}
        title="Tableau de bord"
        subtitle="Vue d'ensemble de l'activité"
      />

      {loading && !data ? (
        <ActivityIndicator style={styles.loader} color={colors.primary} />
      ) : (
        <>
          <View style={[styles.kpis, !isDesktop && styles.kpisMobile]}>
            {kpis.map((kpi) => (
              <StatCard
                key={kpi.label}
                label={kpi.label}
                value={kpi.value}
                icon={kpi.icon}
                tone={kpi.tone}
                compact={!isDesktop}
                style={isDesktop ? styles.kpiDesktop : null}
              />
            ))}
          </View>

          <View style={[styles.grid, isDesktop && styles.gridDesktop]}>
            {chartData.length > 0 ? (
              <Card style={[styles.chartCard, isDesktop && styles.chartCardDesktop]}>
                <SectionHeader title="Évolution mensuelle" subtitle="Chiffre d'affaires par mois" />
                <View
                  onLayout={(event) => {
                    const next = Math.max(120, Math.round(event.nativeEvent.layout.width));
                    setChartWidth((prev) => (prev === next ? prev : next));
                  }}
                >
                  {chartWidth > 0 && (
                    <LineChart
                      key={colors.mode}
                      data={{
                        labels: chartLabels,
                        datasets: [{ data: chartData.length > 0 ? chartData : [0] }],
                      }}
                      width={chartWidth}
                      height={220}
                      withInnerLines
                      withOuterLines={false}
                      chartConfig={{
                        backgroundColor: colors.surface,
                        backgroundGradientFrom: colors.surface,
                        backgroundGradientTo: colors.surface,
                        fillShadowGradientFrom: colors.primary,
                        fillShadowGradientFromOpacity: 0.18,
                        fillShadowGradientTo: colors.primary,
                        fillShadowGradientToOpacity: 0,
                        decimalPlaces: 0,
                        color: () => colors.primary,
                        labelColor: () => colors.textSecondary,
                        propsForBackgroundLines: { stroke: colors.divider, strokeDasharray: '' },
                        propsForDots: { r: '4', strokeWidth: '2', stroke: colors.surface, fill: colors.primary },
                      }}
                      bezier
                      style={styles.chart}
                    />
                  )}
                </View>
              </Card>
            ) : null}

            <View style={[styles.shortcuts, isDesktop && styles.shortcutsDesktop]}>
              <SectionHeader title="Raccourcis" />
              {shortcuts.map((shortcut) => (
                <Card key={shortcut.label} onPress={shortcut.onPress} style={styles.shortcut} accessibilityLabel={shortcut.label}>
                  <View style={styles.shortcutIcon}>
                    <MaterialCommunityIcons name={shortcut.icon} size={22} color={colors.textPrimary} />
                  </View>
                  <View style={styles.shortcutText}>
                    <Text style={styles.shortcutLabel}>{shortcut.label}</Text>
                    <Text style={styles.shortcutHint} numberOfLines={1}>{shortcut.hint}</Text>
                  </View>
                  <MaterialCommunityIcons name="chevron-right" size={22} color={colors.textMuted} />
                </Card>
              ))}
            </View>
          </View>
        </>
      )}
    </Screen>
  );
}

const useStyles = makeStyles((c) => ({
  loader: { marginTop: 40 },
  kpis: { flexDirection: 'row', flexWrap: 'wrap', gap: 16, marginBottom: 32 },
  kpisMobile: { flexDirection: 'column', gap: 10, marginBottom: 24 },
  kpiDesktop: { minWidth: 220 },
  grid: { gap: 24 },
  gridDesktop: { flexDirection: 'row', alignItems: 'flex-start' },
  chartCard: { overflow: 'hidden' },
  chartCardDesktop: { flex: 2 },
  chart: { marginLeft: -12, borderRadius: RADIUS.md },
  shortcuts: { gap: 12 },
  shortcutsDesktop: { flex: 1, minWidth: 300 },
  shortcut: { flexDirection: 'row', alignItems: 'center', gap: 14, paddingVertical: 16, paddingHorizontal: 16 },
  shortcutIcon: {
    width: 44,
    height: 44,
    borderRadius: RADIUS.md,
    backgroundColor: c.surfaceAlt,
    alignItems: 'center',
    justifyContent: 'center',
  },
  shortcutText: { flex: 1, minWidth: 0 },
  shortcutLabel: { fontSize: 15, fontWeight: '700', color: c.textPrimary },
  shortcutHint: { fontSize: 13, color: c.textSecondary, marginTop: 2 },
}));
